import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../../services/loanService';
import { formatDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../hooks/useAuth';

const STATUS_META = {
  pending:      { label: 'Pending',      color: '#ff9900' },
  under_review: { label: 'Under Review', color: '#5B8EFF' },
  approved:     { label: 'Approved',     color: '#00C896' },
  rejected:     { label: 'Rejected',     color: '#FF4757' },
  escalated:    { label: 'Escalated',    color: '#818CF8' },
  cancelled:    { label: 'Cancelled',    color: '#94a3b8' },
  executed:     { label: 'Executed',     color: '#00C896' },
};

const PRIORITY_META = {
  low:      { label: 'Low',      color: '#94a3b8' },
  medium:   { label: 'Medium',   color: '#5B8EFF' },
  high:     { label: 'High',     color: '#ff9900' },
  critical: { label: 'Critical', color: '#FF4757' },
};

function Badge({ value, meta }) {
  const m = meta[value] || { label: value, color: '#94a3b8' };
  return (
    <span style={{
      fontSize: '0.65rem', fontWeight: 700, padding: '0.2rem 0.6rem',
      borderRadius: 99, textTransform: 'uppercase', letterSpacing: '0.05em',
      background: `${m.color}18`, color: m.color, border: `1px solid ${m.color}30`,
    }}>{m.label}</span>
  );
}

export default function ApprovalQueue() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-approvals', statusFilter, moduleFilter],
    queryFn: () => adminService.getApprovals({
      ...(statusFilter && { status: statusFilter }),
      ...(moduleFilter && { module: moduleFilter }),
    }).then(r => r.data.data),
  });

  const requests = data?.results || data || [];

  return (
    <div>
      <div className="page-header">
        <h1>Approval Queue</h1>
        <p>Maker-checker workflow — review and decide on pending requests</p>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <select className="form-input" style={{ width: 160 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          {Object.entries(STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
        </select>
        <select className="form-input" style={{ width: 160 }} value={moduleFilter} onChange={e => setModuleFilter(e.target.value)}>
          <option value="">All Modules</option>
          {['loans', 'disbursements', 'refunds', 'kyc', 'fraud', 'system', 'api', 'customers'].map(m => (
            <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>
          ))}
        </select>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {isLoading ? <div style={{ padding: '2rem' }}><LoadingSpinner /></div> : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Reference</th><th>Title</th><th>Module</th><th>Maker</th>
                  <th>Priority</th><th>Status</th><th>Created</th><th></th>
                </tr>
              </thead>
              <tbody>
                {requests.length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-3)', padding: '2rem' }}>No requests found.</td></tr>
                ) : requests.map(req => (
                  <tr key={req.id} style={req.is_overdue ? { background: 'rgba(255,71,87,0.04)' } : {}}>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {req.reference_number}
                      {req.is_overdue && <span style={{ marginLeft: 6, fontSize: '0.6rem', color: '#FF4757', fontWeight: 700 }}>OVERDUE</span>}
                    </td>
                    <td style={{ maxWidth: 200 }}>
                      <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{req.title}</div>
                      {req.description && <div style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginTop: 2 }}>{req.description.slice(0, 60)}{req.description.length > 60 ? '…' : ''}</div>}
                    </td>
                    <td><span style={{ fontSize: '0.75rem', textTransform: 'capitalize' }}>{req.module}</span></td>
                    <td style={{ fontSize: '0.8rem' }}>{req.maker_email}</td>
                    <td><Badge value={req.priority} meta={PRIORITY_META} /></td>
                    <td><Badge value={req.status} meta={STATUS_META} /></td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>{formatDate(req.created_at)}</td>
                    <td>
                      {(req.status === 'pending' || req.status === 'under_review') &&
                        req.maker_email !== user?.email && (
                        <button className="btn btn-primary" style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem' }}
                          onClick={() => navigate(`/admin/applications/${req.target_id}`)}>
                          Review
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
}
